import {
  Axis,
  Chart,
  ChartSettings,
  LabelSettings,
  Legend,
  TextPositioning,
  WidgetVizType,
} from './widget.models';
import {
  DimensionAttachTypeId,
  DimensionJoinTypeId,
  NomObjectLifecycleStatus,
  NomPostType,
  SortDirection,
} from '../constants';
import {
  FeedPostViewMode,
  WidgetViewProperties,
} from 'src/app/api/services/feed-api.service';

import { Alert } from './alert.models';
import { AnyFilterItem } from '../reducers/feed.reducer';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Principal } from './object.models';
import { DataObjectRef } from './object-ref.models';

export interface ObjectDefinition {
  object_class_id: number;
  object_type_id?: number;
  object_id: string;
}

// SimpleUserTransformer
export interface ObjectUserRef {
  id: string;
  name: string;
  email: string;
  avatar_url: string;
  image_url: string;
}

export interface User {
  id: string;
  username: string;
  display_name: string;
  email: string;
  email_alerts: null | any;
  avatar_url: string;
  company_id: string;
  active_company_id: string;
  company_name: string;
  timezone: NomItem;
  language: Language;
  country_id: string;
  twitter: null | string;
  linkedin: null | string;
  post_code: null | string;
  address: string;
  mobile_phone: string;
  skype: null | string;
  object_id: string;
  object_class_id: number;
  date_time_formats: {
    s_d: number;
    s_t: number;
    s_dt: number;
    l_d: number;
    l_t: number;
    l_dt: number;
    ld_dt: number;
  };
  number_formats: {
    locale: string;
  };
  feature_components: Record<number, boolean>;
  roles: Array<{
    id: number;
    title: string;
    description: string;
    lifecycle_status: number;
  }>;
  groups: any[];
  user: {
    id: string;
    name: string;
    avatar_url: string;
  };
  platform_invitation: {
    is_invited: boolean;
    invited_by: null | any;
    invitation_timestamp: null | any;
    /** Most recent invitation (SEN-5260); the first one stays in time_invited. */
    time_last_invited?: TimeInfo | null;
    invited_count?: number;
  };
  push_services: any;
  external_service_ids: Array<number>; // the active external service ids
  is_enabled: boolean;
  time_created: TimeInfo;
  time_last_active: TimeInfo;
  companies: any[];
  auth_token?: string;
  workflows: Workflows;
  panes: any[];
  context_settings: any;
  company: {
    id: string;
    name: string;
    object_id: string;
    object_class_id: number;
    logo_url: string;
    start_date?: number; // Unix timestamp
    end_date?: number; // Unix timestamp
    colors: {
      icons: string[];
    };
    color_stops: [number, string][];
    color_scheme: string[];
    feature_permissions: {
      id: number;
      name: string;
      access: boolean;
    }[];

    date_time_formats: {
      s_d: string;
      s_t: string;
      s_dt: string;
      l_d: string;
      l_t: string;
      l_dt: string;
      ld_dt: string;
    };
    number_formats: {
      locale: string;
    };
    roles: {
      admin: {
        id: number;
        title: string;
      };
      user: {
        id: number;
        title: string;
      };
    };
    has_instagram_business_account: boolean;
    available_social_source_ids: {
      [key: string]: number;
    };

    timezone?: NomItem;
  };
  default_company: {
    id: string;
    name: string;
  };
  debugging: boolean;
  debug_level: string;
  user_chart_language_id: number;
  dark_mode: boolean;
  lifecycle_status_objects: number[];
  lifecycle_status_objects_loaded: number[];
  list_company_objects: boolean;
  lifecycle_status: number;
  access_level_id: number;
  /** Legacy menu width flag, read once as the `ov-main-menu` default until a preset exists (removal SEN-5379). */
  menu_state_open?: boolean;

  user_access_level: NomItem;
}

interface WorkflowStage {
  id: number;
  name: string;
  auto_share_to_query_id: string;
  next_workflow_stage_id: number | null;
  prev_workflow_stage_id: number | null;
  color_fg: string;
  color_bg: string;
  order: number;
}

export interface WorkflowStagePost {
  id: number;
  name: string;
  enabled: boolean;
  is_next: boolean;
  is_current: boolean;
  is_previous: boolean;
  active_color_fg: string;
  active_color_bg: string;
  inactive_color_fg: string;
  inactive_color_bg: string;
}

export interface WorkflowPostStatus {
  stage_id: number;
  stages: WorkflowStagePost[];
}

export interface Workflow {
  id: number;
  name: string;
  stages: WorkflowStage[];
}

export type Workflows = Workflow[];

export interface MailSettings {
  smtp_host: string;
  smtp_port: string | number;
  smtp_username: string;
  smtp_password: string;
  smtp_security: string;
  from_email: string;
  from_name: string;
}

export interface Company {
  // core object info
  id: string; // or number, depending on your backend; was string before
  object_class_id: number;

  // NEW / migrated fields
  name_i18n: Record<string | number, string>;
  legal_name: string;
  alias: string;

  website_url: string;
  description: string;

  country: NomItem;

  address_line1: string; // legacy
  address_line2: string; // legacy

  city: NomItem;

  postal_code: string;
  address_line_1: string; // new naming
  address_line_2: string; // new naming
  address_note: string;

  phone: string;

  is_enabled: boolean | 0 | 1;
  account_status_id: number | null;
  account_type_id: number;

  client_account_number: string;

  business_entity_type: NomItem;
  business_industry: NomItem;
  business_sector: NomItem;

  market_id: number | null;

  notes: string;

  billing_company_id: number | null;
  referral_company_id: number | null;
  parent_company_id: number | null;

  account_manager_id: number | null;

  entity_reg_id: string | null;
  entity_reg_id_type_id: number | null;

  entity_tax_id: string;
  is_vat_registered: boolean | 0 | 1;

  features: any;

  mailserver_configuration: any;

  default_language_id: number | null;
  default_time_zone_id: number | null;
  default_date_time_formats: any;
  default_number_formats: any;

  company_admin_id: number | null;
  default_admin_role_id: number | null;
  default_user_role_id: number | null;

  allowed_domains_id: number | null;
  allowed_domains: string[] | null;

  default_password_complexity_id: number | null;
  default_password_expiration: number | null;
  default_password_min_length: number | null;
  default_session_timeout: number | null;

  default_output_settings_id: number | null;
  default_output_settings_type_id: number | null;

  lifecycle_status: string | null;
  has_instagram_business_account: boolean | 0 | 1;

  // LEGACY fields (kept for backward compatibility with old UI)
  is_active: boolean | 0 | 1;
  company_name: string;
  company_alias: string;
  vat: string;

  has_custom_domain: boolean;
  custom_domain: string;
  logo: string | null;

  mail_settings: MailSettings;

  account_type: NomItem;
  relationship_type: NomItem;

  admin_user: any;

  profit_center: NomItem;
  partner_agency: NomItem;

  market: NomItem;

  time_created: TimeInfo;
  time_modified: TimeInfo;
  time_last_active: TimeInfo;

  period_start: TimeInfo;
  period_end: TimeInfo;

  company_use_relative_start_period_date: boolean;
  company_period_start_date: number;
  company_period_end_date: number;
  company_period_start_date_relative: number;

  company_chart_color_scheme: string[] | null;
  // NOTE: name aligned with transformer: company_chart_heatmap_color_scheme
  company_chart_heatmap_color_scheme: string[] | null;
}

export interface CompanyFeature {
  id: number;
  name: string;
  type: 'component' | 'feed';
  enabled: boolean;
  max_number: number;
  used: number;
  description: string;
}

export interface ExportTemplate {
  id: number;
  object_class_id: number;
  lifecycle_status: number;
  name: string;
  file_path: string;
  /** Set by ExportTemplateTransformer when a preview image exists (GET /export_template/get_template_thumbnail). */
  has_thumbnail?: boolean;
  language: NomItem;
  output_format: NomItem;
  company: NomItem;
  owner: ObjectUserRef;
  modified_by_user: ObjectUserRef;
  time_created: TimeInfo;
  time_modified: TimeInfo;
  companies?: Array<NomItem>;
  is_default?: boolean; // in company context default settings
}

// BE TransformerFacade::timeInfo() - empty (0) timestamps carry null
// absolute/relative so the `?? '—'` placeholder idiom fires.
export interface TimeInfo {
  relative: string | null;
  absolute: string | null;
  timestamp: number;
}

export type ObjectPermission =
  | 'can_manage'
  | 'can_view'
  | 'can_edit'
  | 'can_share'
  | 'can_export'
  | 'can_email'
  | 'can_schedule'
  | 'can_list'
  | 'can_delete';

export interface DataObject {
  id: string;
  object_id?: string;
  object_class_id: number;
  object_type_id: number;
  workspace: { id: string; title?: string } | null;
  workspace_id?: string;
  thumbnail?: string; // URL to the thumbnail image
  title: string;
  name?: string;
  icon?: IconProp;
  color?: string;
  description: string;
  settings: any;
  owner: Principal;
  permissions: Array<number>;
  shared_with: Array<Principal>;
  is_owner: boolean;
  can_share: boolean;
  can_edit: boolean;
  can_view: boolean;
  can_list: boolean;
  can_delete: boolean;

  lifecycle_status: number;
  background_image_url?: string;
  //default_period_in_days?: number;
  workspaceTitle?: string;
  user_settings: UserSettings;
  download_settings?: any | null; // Specify more detailed type if possible
  is_period_locked?: boolean;
  cache_enabled?: boolean;
  state_params?: {
    time_start: number;
    time_end: number;
    page: number;
    items_per_page: number;
  };
  default_color?: string; // Default color for the object, stored in db
  view_properties?: WidgetViewProperties; // View properties for the object
  time_created: string | any;
  time_modified: string | any;
  stats?: DataObjectStats;
}

export interface DataObjectStats {
  feeds?: { total?: number };
  social?: {
    twitter?: { active?: number };
    facebook?: number;
  };
  dimensions?: { total?: number };
  dashboards?: { total?: number };
}

export interface UserSettings {
  edit_mode: boolean;
}

export interface UserGroup {
  id: string;
  title: string;
  description: string;
  companyId: string; // ! <- this field will be removed at some point
  company: {
    id: string;
    name?: string;
  };
  members: Principal[];
  member_ids: string[];
  lifecycle_status: NomObjectLifecycleStatus;
}

export interface Feed extends DataObject {
  id: string;
  workspace_id: string;
  title: string;
  description: string;
  thumbnail?: string;
  href: string;
  company_id: string;
  class: string;
  type: number;
  object_id: string;
  object_class_id: number;
  object_type_id: number;
  default_period_in_days: number;
  is_screen_capture_enabled: boolean;
  auto_face_recognition_enabled: boolean;
  cache_enabled: boolean;
  is_paused: boolean;
  is_period_locked: boolean;
  timestamp_paused: number;
  title_i18n: { [key: string]: string };
  default_color: string;
  dimensions_linked_list: any[];
  query: string;
  query_created_from: 'builder' | 'editor';
  views: FeedAllowedViewsValuesArray;
  view_posts_mode: FeedPostViewMode;
  combined_feeds: any[];
  state_params: {
    time_start: number;
    time_end: number;
    page: number;
    items_per_page: number;
  };
  social_facebook_comments_status: number;
  social_sources: any[];
  geo_logical_operator_type: any;
  sort_direction: SortDirection;
  order_field: string;
  post_time_mode: 'relative' | 'absolute';
  post_time_field?: 'post_time_published' | 'post_time_created';
  post_display_thumbnail: boolean;
  filter_queries: any[];
  output_settings_id: number;
  output_settings_type_id: number;
  dimensions: Dimension[];
  permissions: any[];
  is_auto_refresh: boolean;
  auto_refresh_seconds: number;
  default_export_language_id: number;
  company_categories: any[];
  workflow_id: number;
  count_selected_posts: number;
  view_properties: WidgetViewProperties;
  additional_sentiment_running: boolean;
  additional_sentiment_algorithm_id: any;
  additional_sentiment_algorithm_start_date: any;
  is_live_stream_enabled: boolean;
  rss_feed_url: string;
  assigned_algorithms: any[];
  full?: boolean;
  download_settings: {
    output_settings_id: number;
    output_settings_type_id: 3;
    formats: Array<number>;
    languages: Array<number>;
  };
  share_settings: {
    output_settings_id: number;
    output_settings_type_id: 3;
    formats: Array<number>;
    languages: Array<number>;
  };
  export_schedules?: ExportSchedule[];
  alerts?: Alert[];

  count_alerts: number;
  count_export_schedules: number;
  count_posts: number;
  count_widgets: number;
  color?: string;
}

export interface PublishTime {
  relative: string;
  timestamp: number;
  absolute: string;
}

export interface Notification {
  fingerprint_id: string;
  template_id: number;
  notification_text_template: string | null;
  notification_text_finished: string | null;
  notification_severity_id: number | null;
  has_progress: boolean;
  has_steps: boolean;
  step: number | null;
  step_max: number | null;
  step_progress: number | null;
  step_progress_max: number | null;
  overall_progress: number | null;
  overall_progress_percent: number | null;
  overall_progress_max: number | null;
  is_finished: boolean | null;
  is_read: boolean; // Typically 0 (unread) or 1 (read)
  time_sent: number;
  payload: any; // Use a more specific type if available
  publish_time: PublishTime;
}

export interface Dimension extends Feed {
  join_type_id: DimensionJoinTypeId;
  title_i18n: Record<string, string>; // Internationalized titles
  workspace_id: string; // Workspace ID
  edit_mode: 'simple' | 'advanced'; // Edit mode (simple or advanced)
  class: string; // Class type (e.g., "dimension")
  simple: Array<{ [key: string]: string | number | boolean }>; // Simple filters (structure depends on the data)
  advanced: string; // Advanced query string
  query: string; // Query editor's human-readable query
  fq: string; // Solr query
  count_used: number; // Number of times used as a dimension
  attach_type_id: DimensionAttachTypeId; // Attachment type ID
  active: boolean; // Whether the dimension is active
  selected: boolean; // Whether the dimension is selected
  default_color: string | null; // Default color
}

export interface ThemeColors {
  light: string;
  dark: string;
}

export interface DashboardPage {
  id: number;
  name: string;
  description?: string;
  page_order: number;
  // Duration (only page-level customizable property)
  duration_seconds: number | null; // null = inherit from global
  period: Period; // * null -> inherit from global, value -> custom override
  is_visible_in_presentation: boolean;
}

/**
 * The `ov-dashboard` view preset's `settings` blob - the page navigator's collapse
 * state. Not a field on `Dashboard`: it lives in the query cache behind
 * `injectDashboardUIStateQuery`.
 */
export interface DashboardViewPreset {
  pagination_menu_open: boolean;
}

/** `ov-main-menu`: the main menu state per user, company-independent (WAP-1706). */
export interface MainMenuViewPreset {
  /** The state the menu returns to from the hover bar. */
  pinnedMode: 'expanded' | 'small';
  /** The menu is folded to the hover bar (no UI yet, carried for the Bar step). */
  barCollapsed: boolean;
  /** `MenuItem.key` of every collapsed header group. */
  collapsedGroups: string[];
}

export interface Dashboard extends DataObject {
  thumbnail?: string;
  export_schedules?: ExportSchedule[];
  is_auto_refresh?: boolean;
  auto_refresh_seconds?: number;
  color?: string;
  company_id: string;

  count_export_schedules?: number;
  view_properties?: WidgetViewProperties;
  // * multi-page Dashboard support
  pages_count: number;
  pages: DashboardPage[];
  presentation_settings: DashboardPresentationSettings;
  selected_page_id: number;
}

export interface DashboardPresentationSettings {
  // * Logo visibility
  show_sensika_logo: boolean;
  show_company_logo: boolean;

  // * Dashboard Title
  dashboard_title_visible: boolean;
  dashboard_title_font_size: number; // 12-42px range
  dashboard_title_font_weight: number; // 400, 500, 600
  dashboard_title_color: ThemeColors;

  // * Page Title (global defaults)
  page_title_visible: boolean;
  page_title_font_size: number; // 12-42px range
  page_title_font_weight: number; // 400, 500, 600
  page_title_color: ThemeColors;

  // * Time Period
  time_period_visible: boolean;
  time_period_font_size: number; // 12-42px range
  time_period_font_weight: number; // 400, 500, 600
  time_period_color: ThemeColors;

  // * Timers (Auto-Reload & Next Slide)
  timer_font_size: number; // 12-16px range
  timer_font_weight: number; // 400, 500, 600
  timer_color: ThemeColors;

  // * Pagination
  pagination_visible: boolean;
  pagination_font_size: number; // 12-42px range
  pagination_font_weight: number; // 400, 500, 600
  pagination_color: ThemeColors;

  // * Presentation Controls
  show_controls: boolean;

  // * Existing properties
  mode: 'manual' | 'auto';
  show_timer: boolean;
  duration_seconds: number; // * default duration (5s)
}

// * Default values for DashboardPresentationSettings
export function getDefaultPresentationSettings(): DashboardPresentationSettings {
  return {
    // * Logo visibility
    show_sensika_logo: true,
    show_company_logo: true,

    // * Dashboard Title
    dashboard_title_visible: true,
    dashboard_title_font_size: 16,
    dashboard_title_font_weight: 500,
    dashboard_title_color: {
      light: '#000000',
      dark: '#ffffff',
    },

    // * Page Title (global defaults)
    page_title_visible: true,
    page_title_font_size: 16,
    page_title_font_weight: 500,
    page_title_color: {
      light: '#000000',
      dark: '#ffffff',
    },

    // * Time Period (blue for both themes)
    time_period_visible: true,
    time_period_font_size: 16,
    time_period_font_weight: 500,
    time_period_color: {
      light: '#0494eb',
      dark: '#0494eb',
    },

    // * Timers (blue for both themes)
    timer_font_size: 12,
    timer_font_weight: 500,
    timer_color: {
      light: '#0494eb',
      dark: '#0494eb',
    },

    // * Pagination
    pagination_visible: true,
    pagination_font_size: 16,
    pagination_font_weight: 500,
    pagination_color: {
      light: '#000000',
      dark: '#ffffff',
    },

    // * Presentation Controls
    show_controls: true,

    // * Existing properties (Initial properties before new properties were added)
    mode: 'manual',
    show_timer: false,
    duration_seconds: 5,
  };
}

export interface Workspace extends DataObject {
  thumbnail?: string;
  company_id: string;
}

export interface UserObjectsResponse {
  workspaces: Array<Workspace>;
  feeds: Array<Feed>;
  dashboards: Array<Dashboard>;
  dimensions: Array<Dimension>;
  export_schedules?: Array<ExportSchedule>;
  alerts?: Array<Alert>;
}

export interface NomItem {
  id: number | string;
  name: string;
}

export interface PermittedCompanyItem extends NomItem {
  is_active: boolean;
  alias: string;
  account_type: {
    id: number;
    name: string;
  };
  country: {
    id: number;
    name: string;
  };
  parent_company: {
    id: string;
    name: string;
  };
}

// models/post.model.ts
export interface Post extends DataObject {
  title: string;
  content: string;
  sentiment_value_auto: number;
  sentiment_value_original: {
    text: string;
    id: number;
  };
  thumbnail: string;
  thumbnail_big: string;
  url: string;
  publish_time: {
    relative: string;
    timestamp: number;
    absolute: string;
  };
  source: {
    title: string;
    title_full: string;
    icon_url: string;
    url: string;
    id: string;
    grank: number;
    crank: number;
    object_id: string;
    object_class_id: number;
  };
  section: {
    title: string;
    id: string;
  };
  social_source_id?: number;
  selected: boolean;
  attachments: any[]; // Can be improved with specific type if known
  user_categories: any[]; // Can be improved with specific type if known
  content_provider: NomenclatureItem;
  reach: number;
  language: NomenclatureItem;
  country: {
    id: number;
    name: string;
  };
  post_type_id: NomPostType;
  post_object_type_id: number;
  post_screen_capture?: string; // Nullable — absolute URL of the post's screen capture
  related_posts: any[]; // Can be improved with specific type if known
  has_synopsis: boolean;
  has_transcription: boolean;
  has_translation: boolean;
  has_error: {
    date_error: boolean;
  };
  color_bg?: any; // Nullable, can be improved with specific type if known
  completeness_score: number;
  post_related_to_parent_id: any[]; // Can be improved with specific type if known
  post_parent_related_count: number;
  post_child_related_count: number;
  post_video_transcript?: any; // Nullable, can be improved with specific type if known
  synopsis?: any; // Nullable, can be improved with specific type if known
  translation?: any; // Nullable, can be improved with specific type if known
  is_post_locked: boolean;
  linked_to_quick_story_report: boolean;

  author: {
    id?: any; // Nullable, can be improved with specific type if known
    name: string; // This is the display name
    avatar_url?: any; // Nullable, can be improved with specific type if known
    profile_url?: any; // Nullable, can be improved with specific type if known
    username?: string; // Social media -> @handle/username
    external_user_id?: string;
  };
  type: 'post' | 'social';
  workflows?: {
    [workflowId: string]: WorkflowPostStatus;
  };
  post_sentiment_algorithm_list: Array<SentimentAlgorithmn>;
  quick_story_report?: QuickStoryReportPost;
  parent?: Post;
  depth?: number; // Nullable, can be improved with specific type if known
  created_time: TimeInfo;
  is_parent?: boolean; // Flag to indicate if this post is a parent in a hierarchy
  is_collapsed?: boolean; // Flag to indicate if the post is collapsed in the UI
  has_parent_thread?: boolean; // Flag to indicate if the post has a parent thread
  parent_thread_expanded?: boolean; // Flag to indicate if the parent thread is expanded in the UI
  parent_thread_index?: number; // Index of the post in the parent thread
  story_id?: string; // ID of the story this post belongs to, if applicable
  post_id?: string; // Story rows only: id of the story's representative DataObject_Post (the row's own `id` is the story's)
}

// the key is the language id of the quick story report

export interface QuickStoryReportPost {
  [key: string]: QuickStoryReport; // key here is language id
}

export interface QuickStoryReport {
  links: QuickStoryReportLink[];
  created_by: {
    id: string;
    username: string;
  };
  created: string;
  html: string;
  language_id: number;
}

interface QuickStoryReportLink {
  original_url: string;
  short_url: string;
  source_brand_name: string;
}

export interface SentimentAlgorithmn {
  sentiment_algorithm_id: number;
  sentiment_id: number;
  original_sentiment_id: number | null;
  is_manual: boolean;
  is_default: boolean;
  id: number;
  algorithm_id: number;
  title: string;
  algorithm_title: string;
}

export interface Language {
  id: number;
  name: string;
  code: string;
  rtl: boolean;
}

export interface Connector extends DataObject {
  id: string;
  title: string;
  description: string;
  short_description: string;
  icon_css: string;
  icon_uri: string;
  object_type: {
    id: number;
    title: string;
  };
  can_create_recurring_rules: boolean;
  can_create_historical_rules: boolean;
  recurring_rules_limit: number;
  object_class_id: number;
  object_type_id: number;
}

export interface ConnectorFull {
  id: string;
  title: string;
  can_create_historical_rules: boolean;
  can_create_recurring_rules: boolean;
  description: string;
  historical_rules_records_limit: number;
  historical_rules_records_limit_reached: boolean;
  historical_rules_records_used: number;
  icon_css: string;
  icon_uri: string;
  object_class_id: number;
  object_type: {
    id: number;
    title: string;
  };
  object_type_id: number;
  permission_type_id: number;
  recurring_rules_limit: number;
  recurring_rules_limit_reached: boolean;
  recurring_rules_records_limit: number;
  recurring_rules_records_limit_reached: boolean;
  recurring_rules_records_used: number;
  recurring_rules_used: number;
  short_description: string;
}

export interface NomenclatureItem {
  id: number;
  title: string;
  name?: string;
  display_name?: string;
  selected?: boolean;
}

export interface OutputFormat extends NomenclatureItem {
  output_types: NomenclatureItem[];
  output_type_id: number;
}
// models/widget.model.ts
export interface Widget extends DataObject {
  x: number;
  y: number;
  rows: number;
  cols: number;
  loading?: boolean;
  is_fake?: boolean; // flag if the widget is fake placeholder that is not saved in the database
  data?: any;
  // define the properties for a Widget
  widget_type_id?: number;
  title: string;
  title_i18n: { [key: string]: string };
  widget_annotation?: string;
  object_id?: string;
  object_class_id: number;
  page_id?: number;
  settings: WidgetSettings;
  sizeX?: number;
  sizeY?: number;
  minimumSizeX?: number;
  minimumSizeY?: number;
  display_order?: number;
  col: number;
  row: number;
  chart_type?: WidgetVizType;
  chart?: Chart;
  dimensions_pivot?: any[];
  dimensions_filter?: any[];
  datasources?: any[];
  chart_options?: string; // the debug chart options — see WidgetViewStateService
  time_created: TimeInfo;
  time_modified: TimeInfo;

  // ! mitigate types - GridStack options
  w?: number;
  h?: number;
}

export interface ExportSchedule extends DataObject {
  id: string;
  object_class_id: number;
  /** Canonical ref + the grid's display extras (class/status filter columns). */
  context_object: DataObjectRef & {
    class_title?: string;
    lifecycle_status_name?: string;
  };
  emails: string[];
  title: string;
  description: string;
  subject: string;
  languages: NomenclatureItem[];
  formats: OutputFormat[];
  output_settings_id: number;
  export_file_name?: string;
  schedule_type_id: number;
  recurring_period: number;
  recurring_period_type_id: number;
  recurring_period_start_time: string;
  recurring_period_end_time: string;
  schedule_enabled: boolean;
  schedule_cover_period: number;
  schedule_cover_period_end_time: string;
  schedule_weekdays_to_send: (boolean | null)[];
  schedule_day_of_month_to_send: number | null; // 1-31, or SCHEDULE_DAY_OF_MONTH_LAST for month end
  schedule_time_of_day_to_send: string;
  schedule_start_date: number;
  schedule_end_date: number | null;
  schedule_next_trigger_time: number;
  cover_period_cut_of_time_delay: number;
  is_active: boolean;
  list_weekdays: string[];
  list_cover_period: string;
  status: NomenclatureItem;
  time_last_sent: TimeInfo;
  time_next_trigger: TimeInfo;
  recipients_count: number;
  // New optional properties
  channels: string[]; // Array of notification channels (e.g., ['email', 'slack'])
  selected_channels_names: string[];
  mattermost_server_id: number | null; // Nullable server ID for Mattermost
  notification_push_service: number[]; // Array of push service IDs
  external_share_option_id: number | null; // Nullable export share option ID
  timezone_id: number | null; // Nullable timezone ID for scheduling
}

export interface WidgetSettings {
  chart_stacking: string;
  chart_id: number;
  chart_type_id: number;
  datasource_object_id: string[];
  datasource_object_class_id: number[];
  datasource_filter_by_first: boolean;
  show_chart_title: string;
  fill_opacity: number;
  use_stacking: number;
  number_of_items: number;
  activate_others: number;
  icon_uri: string;
  icon_css_class: string;
  widget_portrait_max_width: number;
  widget_portrait_max_height: number;
  widget_landscape_max_width: number;
  widget_landscape_max_height: number;
  export_title: { [key: number]: string };
  show_data_labels: boolean;
  data_labels_format: string;
  show_stack_labels: string;
  stack_labels_format: string;
  show_x_axis_title: boolean;
  show_y_axis_title: string;
  show_y_axis_grid_lines: string;
  show_chart_legend: string;
  pie_view_type: string;
  pie_total_radius: number;
  order_by: string;
  order_by_field?: { filter: string };
  output_settings: any;
  export_formats: number[]; // array of output format ids that are enabled for export for this widget
  section_break_page_break_before: boolean;
  widget_header_color: string;
  post_background_color: string;
  dimension_id_pivot: string[];
  dimension_id_filter: string[];
  chart_type: string;
  widget_preset_id: number;
  widget_viz_type_id: number;
  widget_viz_class_id: number;
  title: TextPositioning;
  legend: Legend;
  chart: ChartSettings;
  data_labels: LabelSettings;
  stack_labels: LabelSettings;
  x_axis: Axis;
  y_axis: Axis;
  // series: Series;
  // wordcloud: Wordcloud;
  // bar: Bar;
  show_x_axis_line: string;
  show_y_axis_line: string;
  show_x_axis_ticks: string;
  show_y_axis_ticks: string;
  show_x_axis_grid_lines: string;
  show_x_axis_labels: string;
  show_y_axis_labels: string;
  show_series_markers: string;
  chart_background_color: string;
  gradient_color_min: string;
  gradeitn_color_max: string;
  y_axis_font_size: number;
  x_axis_font_size: number;
  data_labels_font_size: number;
  datasource_color_id: string[];
  dimension_pivot_color?: string[]; // color for the pivot dimension
  override_output_settings?: boolean;
  feed: any;
  custom_period?: boolean;
}

export type IncludeExclude = 'include' | 'exclude';

// models/filter.model.ts
export interface Filter {
  autocomplete_enabled?: boolean;
  description: string;
  editable?: boolean;
  expanded?: boolean;
  has_more?: boolean;
  items: AnyFilterItem[];
  name: string;
  offset?: number;
  limit?: number;
  property_uri: string;
  property_uri_facet: string;
  loading?: boolean; // when filter items are loading, this state property is set to true
}

export interface BaseFilterItem {
  id?: string;
  title: string;
  checked: boolean;
  active: boolean;
  property_uri: string;
  type: IncludeExclude;
  count_posts?: number;
  filter_title?: string;
}

export interface FilterItem extends BaseFilterItem {
  kind: 'value';
  raw_value: string;
  icon?: string | null;
}

export interface PeriodFilterItem extends BaseFilterItem {
  kind: 'period';
  time_start: number;
  time_end: number;
  icon?: string | null;
}

// models/period.model.ts
export interface Period {
  // define the properties for Period
  startTimestamp: number;
  endTimestamp: number;
}

export interface ContentFilters {
  time_start: number;
  time_end: number;
  offset: number;
  items_count: number;
  sort: SortDirection;
  selected_only: boolean;
  order: string; // or 'post_time_published'; if it's the only possible value
  filter_query?: string; // the query from the filter editor
  filters?: Record<string, { include: any[]; exclude: any[] }>;
}

// query builder contexts

export interface QueryBuilderContext {
  key: string;
  property_uri: string;
  autocomplete_enabled: boolean;
  name: string;
  description: string;
  methods: QueryBuilderContextMethod[];
}

export interface QueryBuilderContextMethodItemsMap {
  [key: number]: QueryBuilderContextMethodItem;
}

export interface QueryBuilderContextMethod {
  name: string;
  operator: string;
  items: QueryBuilderContextMethodItem[];
}

export interface QueryBuilderContextMethodItem {
  text: string;
  raw_value: string;
}

export interface ObjectDownloadResponse {
  log_item: OutputLogItem;
  download_url: string;
}
export interface OutputLogItem {
  output_time: number;
  time: {
    relative: string;
    absolute: string;
  };
  output_errors: boolean;
  output_type: {
    id: number;
    name: string;
    icon_uri: string;
  };
  files: FileDetail[];
  object_class_name: string;
  object_type_name: string;
  object_name: string;
  company: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatar_url: string;
    image_url: string;
  };
}

interface FileDetail {
  name: string;
  base_name: string;
  language: string;
  output_format: {
    id: number;
    name: string;
    icon_uri: string;
  };
}

export interface CompanyCategory {
  company_category_id: number;
  company_id: string;
  company_category_name: string;
  company_category_description: string;
  is_active: number;
  count_selected: number;
  categories: UserCategory[];
  companies: any[];
}

export interface UserCategory {
  id: number;
  parent_user_category_id: number;
  company_category_id?: number;
  name: string;
  description?: string;
  display_order?: number;
  is_default?: number;
  color_fg?: string;
  color_bg?: string;
  icon_uri?: string;
  is_constant_published?: number;
  constant_name?: string | null;
  lifecycle_status?: number;
  is_selected?: boolean;
  selected_count?: number | null;
  children: UserCategory[];
  isExpanded?: boolean;
}

export interface PostSynopsis {
  id: number;
  post_id: string;
  language_id: number;
  user_id: string;
  created_timestamp: number;
  modified_timestamp: number;
  title: string;
  synopsis: string;
}

export interface FeedRemovedItem {
  title: string;
  time_removed: number;
  object_id: string;
  object_class_id: number;
  time_published: number;
  url: string;
}

export interface PostTranslation {
  content: string;
  created: string;
  language_id: number;
  title: string;
  updated: string;
}

export interface FaceCoordinates {
  x?: number;
  y?: number;
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
  width: number;
  height: number;
}

export interface LogoSuggestion {
  doc_id: string;
  dist: number;
  brand: string;
}

export interface LogoDetection {
  class_id: number;
  class_name: string;
  confidence: number;
  coordinates: FaceCoordinates;
  image_bytes?: string;
  embedding?: number[];
  brand?: string;
  brand_distance?: number;
  suggestions?: LogoSuggestion[];
}

export interface FacePerson {
  id: number | null;
  name: string;
}

export interface Face {
  id: number;
  post_id: string;
  image_id: number;
  face_id: number | null;
  train_face_id: number | null;
  train_user_id: number | null;
  is_current: number;
  recognition_score: number;
  recognition_status_id: number;
  named_entity_id: number | null;
  time_created: number;
  time_modified: number;
  person: FacePerson;

  coordinates: FaceCoordinates; // coordinates of the face in the image
  embedding?: number[];
  image_bytes: string;
  backend_recognition_status_id: number;
  confidence: number; // confidence of the face recognition
  person_name: string; //populated by the user when the face is recognized
  dirty: boolean;
}

export interface RecentSearch {
  query_text: string;
  filters?: Record<string, any> | null;
  lang?: string | null;
  scope_id?: number | null;
  result_count?: number | null;
  time_modified: number;
}

export interface RecentSearchPayload {
  query_text: string;
  filters?: Record<string, any> | null;
  lang?: string | null;
  scope_id?: number | null;
  result_count?: number | null;
}

export type FeedAllowedViewsValues = 'list' | 'analytics' | 'details';

export type FeedAllowedViewsValuesArray = FeedAllowedViewsValues[];

export enum ExternalService {
  FACEBOOK = 12,
  MATTERMOST_MOE = 16,
  MATTERMOST_TEST = 17,
  MATTERMOST_MOE_PROD = 18,
}

export enum OutputSettingsType {
  DOWNLOAD_SETTINGS = 'download_settings',
  SHARE_SETTINGS = 'share_settings',
}

/**
 * From nom_push_service:
 *  1 → One Signal (mobile push notifications)
 *  2 → Email
 *  3 → Mattermost
 */
export enum NomPushServiceType {
  ONE_SIGNAL = 1,
  EMAIL = 2,
  MATTERMOST = 3,
}

export interface DuplicateObjectErrorItem {
  copy_count: number;
  available_count: number;
  needed_count: number;
  display_order: number;
  name: string;
}

export interface DuplicateObjectErrorResponse {
  error: true;
  errors: Record<string, DuplicateObjectErrorItem>;
}

export interface DuplicateObjectSuccessResponse {
  success: true;
}

export type DuplicateObjectApiResponse =
  | DuplicateObjectSuccessResponse
  | DuplicateObjectErrorResponse;

export interface CopyFeedSuccessResponse {
  object: UserObjectsResponse;
}

export type CopyFeedResponse =
  | CopyFeedSuccessResponse
  | DuplicateObjectErrorResponse;

export interface PlatformIdentity {
  id: number;
  platform_identity_fk: string;
  content_platform: { id: number; name: string };
  source: { id: string; name: string };
  media_category: { id: number; name: string };
  entity_id: number;
  entity_type_id: number;
  username: string;
  name: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  location_name: string;
  location_id: number;
  location_resolved: string | null;
  location_type_id: number;
  country_id: number;
  editorial_country_id: number;
  profile_url: string;
  profile_description: string;
  avatar_url: string;
  is_verified: number;
  verified_type_id: number;
  is_banned: number;
  is_bot: number;
  is_troll: number;
  is_spammer: number;
  is_scammer: number;
  likes_count: number;
  statuses_count: number;
  friends_count: number;
  followers_count: number;
  following_count: number;
  listed_count: number;
  subscribers_count: number;
  spam_report_count: number;
  bot_report_count: number;
  troll_report_count: number;
  scam_report_count: number;
  withheld_count: number;
  suspend_count: number;
  deleted_count: number;
  user_time_created: TimeInfo;
  time_created: TimeInfo;
  time_modified: TimeInfo;
  time_edited: TimeInfo;
}
